import { useState, useEffect } from "react";
import { Link, Navigate, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Shield, Eye, EyeOff, ArrowRight, Heart, Home, Check, Loader2, CreditCard } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { logActivity } from "@/lib/activityLogger";
import { toast } from "sonner";

type Vertical = "life-insurance" | "mortgage" | null;

const Signup = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [step, setStep] = useState(1);
  const [selectedVertical, setSelectedVertical] = useState<Vertical>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { session, loading, signUp } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const proposalId = searchParams.get("proposal_id");
  const [proposal, setProposal] = useState<any>(null);
  const [proposalLoading, setProposalLoading] = useState(!!proposalId);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    company: "",
    licenseNumber: ""
  });

  // Load proposal data if coming from payment
  useEffect(() => {
    if (!proposalId) return;

    const loadProposal = async () => {
      const { data, error } = await supabase.rpc("get_proposal_public", {
        proposal_id: proposalId,
      }) as any;

      if (!error && data) {
        setProposal(data);
        // Pre-fill form from proposal
        const nameParts = (data.agent_name || "").split(" ");
        setFormData((prev) => ({
          ...prev,
          firstName: nameParts[0] || "",
          lastName: nameParts.slice(1).join(" ") || "",
          email: data.agent_email || ""
        }));
        // Set vertical from proposal
        if (data.vertical === "life_insurance") {
          setSelectedVertical("life-insurance");
        } else if (data.vertical === "mortgage") {
          setSelectedVertical("mortgage");
        }
        // Skip to step 2 if proposal has vertical
        if (data.vertical) {
          setStep(2);
        }
      }
      setProposalLoading(false);
    };

    loadProposal();
  }, [proposalId]);

  // If user is already logged in but arriving with a proposal_id,
  // check if the logged-in email matches the proposal email.
  // If it matches, redirect to dashboard (existing customer buying a new package).
  // If it doesn't match, sign out so they can create a new account.
  const [signingOut, setSigningOut] = useState(false);

  useEffect(() => {
    if (session && proposalId && !signingOut) {
      const loggedInEmail = session.user?.email?.toLowerCase();
      const proposalEmail = proposal?.agent_email?.toLowerCase();

      // Wait for proposal to load before deciding
      if (proposalLoading) return;

      if (proposalEmail && loggedInEmail === proposalEmail) {
        // Existing customer — don't sign out, send to dashboard
        navigate("/dashboard", { replace: true });
        return;
      }

      // Different email — sign out for new account creation
      setSigningOut(true);
      supabase.auth.signOut().then(() => {
        setSigningOut(false);
      });
    }
  }, [session, proposalId, signingOut, proposal, proposalLoading, navigate]);

  if (loading || proposalLoading || signingOut) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>);

  }

  // Only redirect to dashboard if logged in WITHOUT a proposal
  if (session && !proposalId) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (step === 1 && selectedVertical) {
      setStep(2);
    } else if (step === 2) {
      setIsSubmitting(true);

      // Sign up with Supabase Auth — returns user even when email confirmation is pending
      const { error, user: signedUpUser } = await signUp(formData.email, formData.password);
      if (error) {
        // Handle "user already exists" gracefully
        const msg = error.message?.toLowerCase() ?? "";
        if (msg.includes("already") || msg.includes("exists") || msg.includes("registered")) {
          toast.error(
            "An account with this email already exists. Try signing in or resetting your password.",
            { duration: 8000 }
          );
          navigate(`/login?email=${encodeURIComponent(formData.email)}`);
          setIsSubmitting(false);
          return;
        }
        toast.error(error.message);
        setIsSubmitting(false);
        return;
      }

      const vertical = selectedVertical === "life-insurance" ? "life_insurance" : "mortgage";

      // Store signup data in localStorage so onboarding can recover it
      // after email verification redirect
      localStorage.setItem("tj_signup", JSON.stringify({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        company: formData.company,
        licenseNumber: formData.licenseNumber,
        vertical,
        proposalId: proposalId || null
      }));

      // signedUpUser is available even when email confirmation is pending
      const userId = signedUpUser?.id;

      if (userId && proposal) {
        // Link the auth user to the proposal and agent.
        // Agent upsert is BEST-EFFORT here: when email confirmation is enabled,
        // auth.uid() is NULL (no session yet), so RLS blocks the write.
        // This is expected — the Onboarding flow creates/migrates the agent via
        // resolve_agent_identity after the user verifies their email.
        if (proposal.agent_id) {
          const { error: agentErr } = await supabase.
          from("agents").
          update({
            name: `${formData.firstName} ${formData.lastName}`,
            email: formData.email,
            company: formData.company || null,
            vertical,
            is_active: false
          }).
          eq("id", proposal.agent_id);

          if (agentErr) {
            console.warn("[Signup] Agent update skipped (expected when email confirmation is pending):", agentErr.message);
          }
        } else {
          const { error: agentErr } = await supabase.from("agents").upsert({
            id: userId,
            name: `${formData.firstName} ${formData.lastName}`,
            email: formData.email,
            company: formData.company || null,
            vertical,
            tier: "Silver",
            licensed_states: [],
            lead_preferences: [],
            is_active: false,
            performance_score: 50
          });

          if (agentErr) {
            console.warn("[Signup] Agent upsert skipped (expected when email confirmation is pending):", agentErr.message);
          }
        }

        await (supabase.
        from("contract_proposals" as any).
        update({
          status: "signup_complete",
          auth_user_id: userId
        }).
        eq("id", proposalId) as any);

        toast.success("Account created! Check your email to verify your account, then continue setup.", {
          duration: 8000
        });
        logActivity({
          event_type: "agent_signup",
          actor_id: userId,
          actor_email: formData.email,
          entity_type: "agent",
          entity_id: userId,
          metadata: { proposal_id: proposalId, vertical, has_proposal: true }
        });
        navigate(`/onboarding?proposal_id=${proposalId}`);
        setIsSubmitting(false);
        return;
      } else if (userId) {
        // No proposal — organic signup.
        // Agent upsert is best-effort: may fail when email confirmation is pending
        // (auth.uid() is NULL). Onboarding handles agent creation after verification.
        const { error: agentErr } = await supabase.from("agents").upsert({
          id: userId,
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          company: formData.company || null,
          vertical,
          tier: "Silver",
          licensed_states: [],
          lead_preferences: [],
          is_active: false,
          performance_score: 50
        });

        if (agentErr) {
          console.warn("[Signup] Agent upsert skipped (expected when email confirmation is pending):", agentErr.message);
        }

        toast.success("Account created! Check your email to verify your account, then continue setup.", {
          duration: 8000
        });
        logActivity({
          event_type: "agent_signup",
          actor_id: userId,
          actor_email: formData.email,
          entity_type: "agent",
          entity_id: userId,
          metadata: { vertical, has_proposal: false }
        });
        navigate("/onboarding");
        setIsSubmitting(false);
        return;
      } else {
        // Fallback: user not returned (shouldn't happen, but handle gracefully)
        toast.success("Account created! Please check your email to verify, then sign in.");
      }

      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background overflow-y-auto">
        <div className="w-full max-w-md py-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-8">
            <img alt="Trusted James" className="h-10 w-auto" src="/lovable-uploads/caf4114e-73b9-4a2b-98fd-4216eb9586a4.png" />
          </Link>

          {/* Proposal banner */}
          {proposal &&
          <div className="mb-6 rounded-xl border border-green-200 bg-green-50/50 p-4">
              <div className="flex items-center gap-2 text-green-700 mb-2">
                <CreditCard className="w-4 h-4" />
                <span className="text-sm font-medium">Payment confirmed</span>
              </div>
              <p className="text-sm text-green-600">
                {proposal.total_leads} {proposal.vertical === "life_insurance" ? "Life Insurance" : "Mortgage"} leads
                {" — "}${proposal.total_amount}
              </p>
            </div>
          }

          {/* Progress Indicator */}
          <div className="flex items-center gap-2 mb-8">
            <div className={`h-1.5 flex-1 rounded-full ${step >= 1 ? 'bg-accent' : 'bg-muted'}`} />
            <div className={`h-1.5 flex-1 rounded-full ${step >= 2 ? 'bg-accent' : 'bg-muted'}`} />
          </div>

          {step === 1 ?
          <>
              <div className="mb-8">
                <h1 className="font-display text-3xl font-bold text-foreground mb-2">
                  Choose your specialty
                </h1>
                <p className="text-muted-foreground">
                  Select the industry where you're licensed to receive leads.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 gap-4">
                  <button
                  type="button"
                  onClick={() => setSelectedVertical("life-insurance")}
                  className={`relative p-6 rounded-2xl border-2 text-left transition-all duration-200 ${
                  selectedVertical === "life-insurance" ?
                  "border-accent bg-accent/5" :
                  "border-border hover:border-accent/50"}`
                  }>

                    {selectedVertical === "life-insurance" &&
                  <div className="absolute top-4 right-4 w-6 h-6 rounded-full bg-accent flex items-center justify-center">
                        <Check className="w-4 h-4 text-accent-foreground" />
                      </div>
                  }
                    <div className="w-12 h-12 rounded-xl bg-rose-500/10 flex items-center justify-center mb-4">
                      <Heart className="w-6 h-6 text-rose-500" />
                    </div>
                    <h3 className="font-semibold text-foreground mb-1">Life Insurance</h3>
                    <p className="text-sm text-muted-foreground">
                      Final Expense, IUL, Mortgage Protection
                    </p>
                  </button>

                  <button
                  type="button"
                  onClick={() => setSelectedVertical("mortgage")}
                  className={`relative p-6 rounded-2xl border-2 text-left transition-all duration-200 ${
                  selectedVertical === "mortgage" ?
                  "border-accent bg-accent/5" :
                  "border-border hover:border-accent/50"}`
                  }>

                    {selectedVertical === "mortgage" &&
                  <div className="absolute top-4 right-4 w-6 h-6 rounded-full bg-accent flex items-center justify-center">
                        <Check className="w-4 h-4 text-accent-foreground" />
                      </div>
                  }
                    <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center mb-4">
                      <Home className="w-6 h-6 text-blue-500" />
                    </div>
                    <h3 className="font-semibold text-foreground mb-1">Mortgage</h3>
                    <p className="text-sm text-muted-foreground">
                      Purchase, Refinance, HELOC, Reverse, Cash-Out
                    </p>
                  </button>
                </div>

                <Button
                type="submit"
                variant="gold"
                size="lg"
                className="w-full"
                disabled={!selectedVertical}>

                  Continue
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </form>
            </> :

          <>
              <div className="mb-8">
                {!proposal &&
              <button
                onClick={() => setStep(1)}
                className="text-sm text-accent hover:underline mb-4 inline-flex items-center gap-1">

                    ← Back to specialty selection
                  </button>
              }
                <h1 className="font-display text-3xl font-bold text-foreground mb-2">
                  Create your account
                </h1>
                <p className="text-muted-foreground">
                  Enter your details to get started with Trusted James.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name</Label>
                    <Input
                    id="firstName"
                    placeholder="John"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    className="h-12"
                    required />

                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input
                    id="lastName"
                    placeholder="Smith"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    className="h-12"
                    required />

                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="h-12"
                  required
                  readOnly={!!proposal} />

                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="h-12 pr-12"
                    required />

                    <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">

                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="company">Company / Agency Name</Label>
                  <Input
                  id="company"
                  placeholder="ABC Insurance Agency"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="h-12" />

                </div>

                <div className="space-y-2">
                  <Label htmlFor="licenseNumber">License Number</Label>
                  <Input
                  id="licenseNumber"
                  placeholder="Your state license number"
                  value={formData.licenseNumber}
                  onChange={(e) => setFormData({ ...formData, licenseNumber: e.target.value })}
                  className="h-12"
                  required />

                </div>

                <Button type="submit" variant="gold" size="lg" className="w-full" disabled={isSubmitting}>
                  {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                  Create Account
                  <ArrowRight className="w-4 h-4 ml-1" />
                </Button>

                <p className="text-xs text-muted-foreground text-center">
                  By creating an account, you agree to our{" "}
                  <Link to="/terms" className="text-accent hover:underline">Terms of Service</Link>
                  {" "}and{" "}
                  <Link to="/privacy" className="text-accent hover:underline">Privacy Policy</Link>.
                </p>
              </form>
            </>
          }

          <p className="text-center text-sm text-muted-foreground mt-8">
            Already have an account?{" "}
            <Link to="/login" className="text-accent font-medium hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </div>

      {/* Right Panel - Branding */}
      <div className="hidden lg:flex flex-1 gradient-hero items-center justify-center p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 right-10 w-72 h-72 bg-accent rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-10 w-64 h-64 bg-accent rounded-full blur-3xl" />
        </div>

        <div className="relative z-10 text-center max-w-md">
          <div className="w-24 h-24 rounded-3xl bg-primary-foreground/10 border border-primary-foreground/20 flex items-center justify-center mx-auto mb-8">
            <Shield className="w-12 h-12 text-accent" />
          </div>
          <h2 className="font-display text-3xl font-bold text-primary-foreground mb-4">
            Join our network
          </h2>
          <p className="text-primary-foreground/70 leading-relaxed mb-8">
            Become part of an exclusive community of top-performing agents who trust our platform.
          </p>

          <div className="space-y-4">
            <div className="flex items-center gap-3 text-left bg-primary-foreground/5 rounded-xl p-4 border border-primary-foreground/10">
              <Check className="w-5 h-5 text-accent flex-shrink-0" />
              <span className="text-primary-foreground/80 text-sm">100% exclusive leads—never shared</span>
            </div>
            <div className="flex items-center gap-3 text-left bg-primary-foreground/5 rounded-xl p-4 border border-primary-foreground/10">
              <Check className="w-5 h-5 text-accent flex-shrink-0" />
              <span className="text-primary-foreground/80 text-sm">Fair, performance-based distribution</span>
            </div>
            <div className="flex items-center gap-3 text-left bg-primary-foreground/5 rounded-xl p-4 border border-primary-foreground/10">
              <Check className="w-5 h-5 text-accent flex-shrink-0" />
              <span className="text-primary-foreground/80 text-sm">Dedicated account manager support</span>
            </div>
          </div>
        </div>
      </div>
    </div>);

};

export default Signup;